// Match Lineup — one player node on the pitch (design handoff, ported 1:1).
//
// White-10% circle, 2px border per state, shirt number white 700, name with
// text-shadow below (full name at every width — handoff L10: each player owns
// a full column so the name wraps instead of truncating), captain badge,
// rating chip per ratingTier10 (only when a rating exists — no reserved
// holes, L6), incident stack top-left (max 3 grouped glyphs then "+n"),
// highlight ring for highlightPlayerId. The whole node is one ≥44px click
// target navigating to the player's profile when it is resolvable.

import React from "react";
import { ratingTier10, formatRating10 } from "../../totw/totwUtils";
import { playerGlyphKeys, GroupedIncidentSymbol } from "./incidentSymbols";
import { groupGlyphKeys } from "./lineupUtils";

const PlayerNode = ({
  slot, // { player, top, left, columnWidth } from lineupUtils.layoutTeam
  size = 38,
  nameSize = 12,
  incidentSize = 13,
  highlighted = false,
  onNavigate = null, // (player) => void, null -> node is not interactive
  onSubOut = null, // (player, anchorElement) => void — L12 hover/tap anchor
  onSubOutLeave = null, // () => void — pointer left the out-arrow anchor
}) => {
  const p = slot.player;
  const rating = formatRating10(p.rating);
  const hasRating = rating !== null && Number(p.rating) > 0;
  const tier = hasRating ? ratingTier10(p.rating) : null;
  const grouped = groupGlyphKeys(playerGlyphKeys(p));
  const icons = grouped.slice(0, 3);
  const extra = grouped.length - icons.length;
  const clickable = typeof onNavigate === "function";
  const numSize = size >= 42 ? 15 : size >= 36 ? 13 : 12;
  const badgeOff = size >= 42 ? -2 : 0;
  const badgeSize = size >= 42 ? 11 : 10;

  const handleClick = clickable ? () => onNavigate(p) : undefined;
  const handleKey = clickable
    ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onNavigate(p);
        }
      }
    : undefined;

  return (
    <div
      role={clickable ? "link" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKey}
      style={{
        position: "absolute",
        left: `${slot.left}%`,
        top: `${slot.top}%`,
        transform: "translate(-50%,-50%)",
        width: slot.columnWidth,
        minHeight: 44,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: clickable ? "pointer" : "default",
      }}
    >
      <div
        data-lineup-node="true"
        style={{
          position: "relative",
          width: size,
          height: size,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,255,255,0.10)",
          border: `2px solid ${highlighted ? "#2183FE" : "rgba(255,255,255,0.55)"}`,
          boxShadow: highlighted
            ? "0 0 0 3px rgba(33,131,254,0.45), 0 0 16px 4px rgba(33,131,254,0.55)"
            : "0 1px 3px rgba(2,6,23,0.45)",
        }}
      >
        <div
          data-no-translate="true"
          style={{ fontSize: numSize, fontWeight: 700, color: "#FFFFFF", lineHeight: 1 }}
        >
          {p.shirt_number}
        </div>

        {Number(p.captain) === 1 && (
          <div
            data-no-translate="true"
            style={{
              position: "absolute",
              left: -4,
              bottom: -3,
              width: 14,
              height: 14,
              borderRadius: 4,
              background: "#FFFFFF",
              color: "#0F172A",
              fontSize: 9,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}
          >
            C
          </div>
        )}

        {hasRating && (
          <div
            data-no-translate="true"
            style={{
              position: "absolute",
              right: badgeOff,
              top: badgeOff,
              transform: "translate(50%,-50%)",
              background: tier.bg,
              color: tier.fg,
              fontSize: badgeSize,
              fontWeight: 800,
              padding: "1px 7px",
              borderRadius: 6,
              lineHeight: 1.35,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {rating}
          </div>
        )}

        {icons.length > 0 && (
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              transform: "translate(-60%,-55%)",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {icons.map((ic, i) => {
              const isOut = ic.type === "out" && typeof onSubOut === "function";
              return (
                <div
                  key={`${ic.type}${i}`}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                  onMouseEnter={
                    // Anchor the substitution card on the NODE circle, not
                    // the small arrow glyph — the card must sit beside the
                    // node and never cover it (handoff L12.1).
                    isOut
                      ? (e) =>
                          onSubOut(
                            p,
                            e.currentTarget.closest("[data-lineup-node]") ||
                              e.currentTarget
                          )
                      : undefined
                  }
                  onMouseLeave={
                    isOut && typeof onSubOutLeave === "function"
                      ? () => onSubOutLeave()
                      : undefined
                  }
                  onClick={
                    isOut
                      ? (e) => {
                          e.stopPropagation();
                          onSubOut(
                            p,
                            e.currentTarget.closest("[data-lineup-node]") ||
                              e.currentTarget
                          );
                        }
                      : undefined
                  }
                >
                  <GroupedIncidentSymbol
                    type={ic.type}
                    count={ic.count}
                    size={incidentSize}
                    onDark
                  />
                </div>
              );
            })}
            {extra > 0 && (
              <div
                data-no-translate="true"
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: "#FFFFFF",
                  background: "rgba(15,23,42,0.7)",
                  borderRadius: 3,
                  padding: "0 3px",
                }}
              >
                {`+${extra}`}
              </div>
            )}
          </div>
        )}
      </div>

      <div
        data-no-translate="true"
        style={{
          marginTop: 5,
          width: "100%",
          fontSize: nameSize,
          fontWeight: 600,
          lineHeight: 1.2,
          color: "#FFFFFF",
          textAlign: "center",
          textWrap: "pretty",
          overflowWrap: "break-word",
          hyphens: "auto",
          textShadow: "0 1px 3px rgba(2,6,23,0.85)",
        }}
      >
        {p.name}
      </div>
    </div>
  );
};

export default PlayerNode;
